import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ToolPageShell, inputCls, Field, StatusPill } from "@/components/AdminTools";
import { PackagePlus, Plus, X, Send, Sparkles, MapPin, Calendar, AlertCircle } from "lucide-react";

const PRIORITIES = [
  { v: "normal", label: "Normal" },
  { v: "urgent", label: "Urgent (under 14 days)" },
];

const CATEGORIES = ["sleep", "metabolic", "hormone", "diagnostics", "wellness", "longevity", "other"];

const STATUS_TONE = {
  open: "bg-blue-100 text-blue-700",
  sourcing: "bg-amber-100 text-amber-700",
  matched: "bg-emerald-100 text-emerald-700",
  closed: "bg-slate-100 text-slate-600",
};

const EMPTY = {
  title: "",
  category: "other",
  description: "",
  target_volume_per_month: "",
  target_unit_price: "",
  geography: "",
  needed_by: "",
  priority: "normal",
};

/**
 * "Request Services Not Listed" — clinics submit catalog gaps. Admin triages.
 * Submission lands in /api/service-requests with status=open.
 */
export default function PortalServiceRequests() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/service-requests")
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        target_volume_per_month: form.target_volume_per_month ? Number(form.target_volume_per_month) : null,
        target_unit_price: form.target_unit_price ? Number(form.target_unit_price) : null,
        needed_by: form.needed_by || null,
      };
      await api.post("/service-requests", payload);
      toast.success("Request submitted. Our concierge will source vendors and follow up.");
      setForm(EMPTY); setCreating(false); load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = async (id) => {
    if (!window.confirm("Close this request?")) return;
    try {
      await api.delete(`/service-requests/${id}`);
      toast.success("Request closed.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <ToolPageShell
      eyebrow="Clinic · Concierge"
      title="Request services not listed"
      intro="Need a vendor or program type that isn't in our catalog yet? Submit a request — our concierge sources matches in the network within 7 days."
      actions={
        !creating && (
          <button
            type="button"
            data-testid="service-request-new-btn"
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-800"
          >
            <Plus className="w-3.5 h-3.5" /> New request
          </button>
        )
      }
    >
      {creating && (
        <form
          data-testid="service-request-form"
          onSubmit={submit}
          className="rounded-2xl border border-blue-200 bg-blue-50/40 p-5 space-y-4 mb-5"
        >
          <div className="flex items-start justify-between">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-blue-700 inline-flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> New service request
            </div>
            <button type="button" onClick={() => { setCreating(false); setForm(EMPTY); }} className="text-slate-400 hover:text-slate-700">
              <X className="w-4 h-4" />
            </button>
          </div>

          <Field label="What service or program do you need?">
            <input
              required
              data-testid="service-request-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Compounded peptide therapy delivery, on-site phlebotomy, GLP-2 sourcing"
              className={inputCls}
            />
          </Field>

          <Field label="Description · operational context">
            <textarea
              required
              data-testid="service-request-description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={4}
              placeholder="What problem are you solving for? Cadence (one-time / monthly / quarterly)? Any specific vendor capabilities you need (e.g. cold-chain shipping, attestations, EHR integration)?"
              className={inputCls}
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Category">
              <select
                data-testid="service-request-category"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className={inputCls}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                ))}
              </select>
            </Field>
            <Field label="Priority">
              <select
                data-testid="service-request-priority"
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                className={inputCls}
              >
                {PRIORITIES.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
              </select>
            </Field>
            <Field label="Target volume / month">
              <input
                type="number"
                min="0"
                data-testid="service-request-volume"
                value={form.target_volume_per_month}
                onChange={(e) => setForm({ ...form, target_volume_per_month: e.target.value })}
                placeholder="e.g. 25"
                className={inputCls}
              />
            </Field>
            <Field label="Target unit price ($)">
              <input
                type="number"
                min="0"
                step="0.01"
                data-testid="service-request-price"
                value={form.target_unit_price}
                onChange={(e) => setForm({ ...form, target_unit_price: e.target.value })}
                placeholder="e.g. 195.00"
                className={inputCls}
              />
            </Field>
            <Field label="Geography (optional)">
              <input
                data-testid="service-request-geography"
                value={form.geography}
                onChange={(e) => setForm({ ...form, geography: e.target.value })}
                placeholder="e.g. Texas, Bay Area, Nationwide"
                className={inputCls}
              />
            </Field>
            <Field label="Needed by (optional)">
              <input
                type="date"
                data-testid="service-request-needed-by"
                value={form.needed_by}
                onChange={(e) => setForm({ ...form, needed_by: e.target.value })}
                className={inputCls}
              />
            </Field>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-blue-200">
            <button
              type="button"
              onClick={() => { setCreating(false); setForm(EMPTY); }}
              className="text-xs font-semibold text-slate-600 hover:text-slate-900 px-3 py-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="service-request-submit"
              className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-5 py-2 text-sm font-semibold hover:bg-slate-800 disabled:opacity-60"
            >
              <Send className="w-3.5 h-3.5" />
              {submitting ? "Submitting…" : "Submit to concierge"}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="service-requests-empty">
          <PackagePlus className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <div className="font-display text-lg font-semibold text-slate-900">No requests yet</div>
          <p className="text-sm text-slate-500 mt-1">
            Submit your first request to see how our concierge handles vendor sourcing.
          </p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="service-requests-list">
          {items.map((it) => (
            <li
              key={it.id}
              data-testid={`service-request-${it.id}`}
              className="rounded-2xl border border-slate-200 bg-white p-5"
            >
              <div className="flex items-start gap-3">
                <span className="w-9 h-9 rounded-lg bg-blue-50 text-blue-700 border border-blue-100 flex items-center justify-center shrink-0">
                  <PackagePlus className="w-4 h-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display text-lg font-semibold text-slate-900">{it.title}</h3>
                    <span className={`text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 ${STATUS_TONE[it.status] || "bg-slate-100 text-slate-600"}`}>
                      {it.status}
                    </span>
                    {it.priority === "urgent" && (
                      <span className="text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 bg-rose-100 text-rose-700 inline-flex items-center gap-0.5">
                        <AlertCircle className="w-2.5 h-2.5" /> Urgent
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-slate-700 whitespace-pre-wrap">{it.description}</p>
                  <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-slate-500">
                    {it.category && <Tag>{it.category}</Tag>}
                    {it.target_volume_per_month != null && <Tag>{it.target_volume_per_month}/mo target</Tag>}
                    {it.target_unit_price != null && <Tag>${it.target_unit_price.toLocaleString()} unit target</Tag>}
                    {it.geography && (
                      <Tag><MapPin className="w-2.5 h-2.5 inline mr-0.5" /> {it.geography}</Tag>
                    )}
                    {it.needed_by && (
                      <Tag><Calendar className="w-2.5 h-2.5 inline mr-0.5" /> by {it.needed_by}</Tag>
                    )}
                  </div>
                  {it.admin_notes && (
                    <div className="mt-3 rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-900">
                      <span className="font-bold uppercase tracking-wider text-[10px] block mb-1">Concierge update</span>
                      {it.admin_notes}
                    </div>
                  )}
                </div>
                {it.status !== "closed" && (
                  <button
                    type="button"
                    onClick={() => cancel(it.id)}
                    data-testid={`service-request-close-${it.id}`}
                    className="text-[11px] font-semibold text-slate-500 hover:text-rose-700"
                  >
                    Close
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </ToolPageShell>
  );
}

function Tag({ children }) {
  return (
    <span className="inline-flex items-center rounded-full bg-slate-100 border border-slate-200 px-2 py-0.5 capitalize">
      {children}
    </span>
  );
}

// Used so AdminTools doesn't tree-shake the import accidentally.
void StatusPill;
